import * as z from "zod";
import {
  ANALYTICS_QUERY_LIMITS,
  AnalyticsNeighborKind,
  AnalyticsSpanExtentKind,
  AnalyticsSpanUnit,
  AnalyticsTimeUnitSchema,
} from "./analytics-query-constants";

/**
 * Schema-first primitives: non-generic leaf shapes declared once as a schema,
 * with their types inferred (the V1 `custom-analytics-query-primitives.ts`
 * pattern). Nothing here recurses into an expression.
 */

/**
 * YYYY-MM-DDTHH:MM, optional :SS, optional .fraction. No Z, no ±offset.
 * Merchant-local (C2): the merchant's clock, not UTC.
 */
const analyticsLocalDate =
  "(?:(?:\\d\\d[2468][048]|\\d\\d[13579][26]|\\d\\d0[48]|[02468][048]00|[13579][26]00)-02-29|\\d{4}-(?:(?:0[13578]|1[02])-(?:0[1-9]|[12]\\d|3[01])|(?:0[469]|11)-(?:0[1-9]|[12]\\d|30)|(?:02)-(?:0[1-9]|1\\d|2[0-8])))";
const analyticsLocalTime =
  "(?:[01]\\d|2[0-3]):[0-5]\\d(?::[0-5]\\d(?:\\.\\d+)?)?";
const ANALYTICS_LOCAL_DATETIME_PATTERN = new RegExp(
  `^${analyticsLocalDate}T${analyticsLocalTime}$`,
);

const AnalyticsLocalDatetimeSchema = z
  .string()
  .regex(
    ANALYTICS_LOCAL_DATETIME_PATTERN,
    "A datetime constant is merchant-local: YYYY-MM-DDTHH:MM[:SS], no Z and no offset.",
  );

export const AnalyticsConstantValueSchema = z.discriminatedUnion("type", [
  z.strictObject({
    type: z.literal("text"),
    value: z.string().max(ANALYTICS_QUERY_LIMITS.textMax),
  }),
  z.strictObject({ type: z.literal("quantity"), value: z.number() }),
  z.strictObject({ type: z.literal("money"), value: z.number() }),
  z.strictObject({ type: z.literal("ratio"), value: z.number() }),
  z.strictObject({ type: z.literal("boolean"), value: z.boolean() }),
  z.strictObject({ type: z.literal("date"), value: z.iso.date() }),
  z.strictObject({
    type: z.literal("datetime"),
    value: AnalyticsLocalDatetimeSchema,
  }),
  z.strictObject({
    type: z.literal("duration"),
    value: z.number(),
    unit: AnalyticsTimeUnitSchema,
  }),
  z.strictObject({ type: z.literal("absent") }),
]);

export type AnalyticsConstantValue = z.infer<
  typeof AnalyticsConstantValueSchema
>;

const AnalyticsDistanceSchema = z.union([
  z.number().positive(),
  z.strictObject({
    amount: z.number().positive(),
    unit: AnalyticsTimeUnitSchema,
  }),
]);

const AnalyticsStepsSchema = z
  .int()
  .min(1)
  .max(ANALYTICS_QUERY_LIMITS.stepsMax);

export const AnalyticsNeighborAtSchema = z.discriminatedUnion("kind", [
  z.strictObject({
    kind: z.literal(AnalyticsNeighborKind.Previous),
    steps: AnalyticsStepsSchema.optional(),
  }),
  z.strictObject({
    kind: z.literal(AnalyticsNeighborKind.Next),
    steps: AnalyticsStepsSchema.optional(),
  }),
  z.strictObject({ kind: z.literal(AnalyticsNeighborKind.First) }),
  z.strictObject({ kind: z.literal(AnalyticsNeighborKind.Last) }),
  z.strictObject({
    kind: z.literal(AnalyticsNeighborKind.Nth),
    index: AnalyticsStepsSchema,
  }),
  /** Value-based: the row whose sequence value is exactly `distance` behind or ahead ("the same week last year"). */
  z.strictObject({
    kind: z.literal(AnalyticsNeighborKind.Back),
    distance: AnalyticsDistanceSchema,
  }),
  z.strictObject({
    kind: z.literal(AnalyticsNeighborKind.Ahead),
    distance: AnalyticsDistanceSchema,
  }),
]);

export type AnalyticsNeighborAt = z.infer<typeof AnalyticsNeighborAtSchema>;

const analyticsOpenSpanExtents = [
  z.strictObject({ kind: z.literal(AnalyticsSpanExtentKind.None) }),
  z.strictObject({ kind: z.literal(AnalyticsSpanExtentKind.All) }),
] as const;

export const AnalyticsRowsSpanExtentSchema = z.discriminatedUnion("kind", [
  ...analyticsOpenSpanExtents,
  z.strictObject({
    kind: z.literal(AnalyticsSpanExtentKind.Rows),
    count: z.int().min(1).max(ANALYTICS_QUERY_LIMITS.rowsMax),
  }),
]);

export const AnalyticsTimeSpanExtentSchema = z.discriminatedUnion("kind", [
  ...analyticsOpenSpanExtents,
  z.strictObject({
    kind: z.literal(AnalyticsSpanExtentKind.Time),
    amount: z.number().positive(),
    unit: AnalyticsTimeUnitSchema,
  }),
]);

/** The current row is always included. One unit frames both ends: a SQL frame is `rows` or `range`, never both. */
export const AnalyticsSpanSchema = z.discriminatedUnion("unit", [
  z.strictObject({
    unit: z.literal(AnalyticsSpanUnit.Rows),
    before: AnalyticsRowsSpanExtentSchema,
    after: AnalyticsRowsSpanExtentSchema,
  }),
  z.strictObject({
    unit: z.literal(AnalyticsSpanUnit.Time),
    before: AnalyticsTimeSpanExtentSchema,
    after: AnalyticsTimeSpanExtentSchema,
  }),
]);

export type AnalyticsSpan = z.infer<typeof AnalyticsSpanSchema>;

export type AnalyticsSpanExtent = AnalyticsSpan["before"];
